import { VisibilityState, createColumnHelper } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CSVExport from "cpl-react-common/components/Actions/CSVExport";
import {
  fetchAllUsers,
  fetchUserTableColumnOrder,
  fetchUserTableVisibleColumns,
  selectAllUsers,
  selectIncludeInactive,
  selectIsLoading,
  selectSavedColumnOrder,
  selectVisibleColumns,
  setIncludeInactive,
  updateUserActiveStatus,
  updateUserTableColumnOrder,
  updateUserTableVisibleColumns,
} from "./UserTableSlice";
import { Link } from "react-router-dom";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import ContextMenu from "cpl-react-common/components/ContextMenu/ContextMenu";
import ContextMenuItems from "cpl-react-common/components/ContextMenu/ContextMenuItems";
import ContextMenuItem from "cpl-react-common/components/ContextMenu/ContextMenuItem";
import ImportCoreUsersButton from "./ImportCoreUsersButton";
import { faCheck, faPowerOff, faUser, faUserPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import ResendUserInvitationsButton from "./ResendUserInvitationsButton";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { combineName } from "utils/stringUtility";

const UserTable: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();

  const includeInactive = useSelector(selectIncludeInactive);
  const isLoading = useSelector(selectIsLoading);
  const users = useSelector(selectAllUsers);
  const savedColumnOrder = useSelector(selectSavedColumnOrder);
  const savedVisibleColumns = useSelector(selectVisibleColumns);
  const [userToUpdateStatus, setUserToUpdateStatus] = useState<UserModel>();

  function handleUpdateStatusAnswer(answer: boolean) {
    if (answer && userToUpdateStatus) thunkDispatch(updateUserActiveStatus({ userID: userToUpdateStatus.userID! }));
    setUserToUpdateStatus(undefined);
  }

  function saveColumnOrderCallback(newColumnOrder: string[]) {
    thunkDispatch(updateUserTableColumnOrder(newColumnOrder));
  }

  function saveVisibleColumnsCallback(visibleColumns: VisibilityState) {
    thunkDispatch(updateUserTableVisibleColumns(visibleColumns));
  }

  const columnHelper = createColumnHelper<UserModel>();

  const columnDefinition = [
    columnHelper.display({
      id: "actions",
      cell: (props) => (
        <div className="btn-group btn-group-sm" role="group">
          <Permission permissionPredicate={(permission) => permission === Permissions.ViewUsers}>
            <Link
              data-test-id={`user-table__view-user-profile-link-${props.row.original.userID}`}
              className="btn btn-icon btn-primary"
              to={`/Admin/UserProfile/${props.row.original.userID}`}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="View user profile"
            >
              <FontAwesomeIcon icon={faUser} />
            </Link>
          </Permission>
          <Permission permissionPredicate={(permission) => permission === Permissions.DeactivateUsers}>
            {props.row.original.inactive ? (
              <button
                data-test-id={`user-table__reactivate-user-button-${props.row.original.userID}`}
                className="btn btn-icon btn-success"
                onClick={() => setUserToUpdateStatus(props.row.original)}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Reactivate user"
              >
                <FontAwesomeIcon icon={faPowerOff as IconProp} />
              </button>
            ) : (
              <button
                data-test-id={`user-table__deactivate-user-button-${props.row.original.userID}`}
                className="btn btn-icon btn-danger"
                onClick={() => setUserToUpdateStatus(props.row.original)}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Deactivate user"
              >
                <FontAwesomeIcon icon={faPowerOff as IconProp} />
              </button>
            )}
          </Permission>
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.firstName, {
      id: "First Name",
      cell: (info) => info.getValue(),
      header: () => <span>First Name</span>,
    }),
    columnHelper.accessor((row) => row.lastName, {
      id: "Last Name",
      cell: (info) => info.getValue(),
      header: () => <span>Last Name</span>,
    }),
    columnHelper.accessor((row) => row.emailAddress, {
      id: "Email",
      cell: (info) => info.getValue(),
      header: () => <span>Email</span>,
    }),
    columnHelper.accessor((row) => row.telephoneNumber, {
      id: "Telephone",
      cell: (info) => info.getValue(),
      header: () => <span>Telephone</span>,
    }),
    columnHelper.accessor((row) => row.inactive, {
      id: "Active",
      cell: (info) =>
        info.getValue() ? (
          <FontAwesomeIcon icon={faXmark as IconProp} />
        ) : (
          <FontAwesomeIcon icon={faCheck as IconProp} />
        ),
      header: () => <span>Active</span>,
      enableColumnFilter: false,
    }),
  ];

  useEffect(() => {
    thunkDispatch(fetchAllUsers(includeInactive));
    thunkDispatch(fetchUserTableColumnOrder());
    thunkDispatch(fetchUserTableVisibleColumns());
  }, [thunkDispatch, includeInactive]);

  const csvHeaders = Object.keys(new UserModel());
  const csvData = users;
  const currentDate = new Date();
  const date = `Users Table ${currentDate.getFullYear()}_${currentDate.getMonth() + 1}_${currentDate.getDate()}`;

  return (
    <Permission permissionPredicate={(permission) => permission === Permissions.ViewUsers}>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Users Table</div>
          <div className="card-toolbar gap-2">
            <div className="form-switch form-switch-md form-check-custom form-check-solid">
              <label className="fw-bold text-muted col-form-label">Include Inactive?</label>
              <input
                data-test-id="user-table__include-inactive-checkbox"
                className="form-check-input"
                type="checkbox"
                checked={includeInactive}
                onChange={() => dispatch(setIncludeInactive(!includeInactive))}
              />
            </div>
            <Permission permissionPredicate={(permission) => permission === Permissions.AddUsers}>
              <Link
                data-test-id="user-table__add-new-user-link"
                to="/Admin/Users/NewUser/"
                className="btn btn-primary align-self-center me-1"
              >
                <FontAwesomeIcon icon={faUserPlus} /> Add New User
              </Link>
              <ImportCoreUsersButton />
              <ResendUserInvitationsButton />
            </Permission>
          </div>
        </div>
        <ContextMenu targetId="users-table">
          <ContextMenuItems>
            <ContextMenuItem data-test-id="user-table__export-csv-menu-item" onClick={undefined}>
              <CSVExport
                data-test-id="user-table__export-csv-button"
                headers={csvHeaders}
                data={csvData}
                fileName={date}
                applyStyles={false}
              >
                Export To CSV
              </CSVExport>
            </ContextMenuItem>
          </ContextMenuItems>
          <div className="card-body" id="users-table">
            <YesNoAlert
              data-test-id="user-table__update-user-status-alert"
              showAlert={!!userToUpdateStatus}
              alertTitle={userToUpdateStatus?.inactive ? "Reactivate User" : "Deactivate User"}
              alertText={`Are you sure you want to ${
                userToUpdateStatus?.inactive ? "reactivate" : "deactivate"
              } ${combineName(userToUpdateStatus?.firstName, userToUpdateStatus?.lastName)}?`}
              callBack={handleUpdateStatusAnswer}
              style={Style.Warning}
            />
            {isLoading ? (
              <div>Fetching users</div>
            ) : (
              <>
                <CPLTable
                  data={users}
                  columns={columnDefinition}
                  savedColumnOrder={{
                    savedColumns: savedColumnOrder,
                    saveColumnCallback: saveColumnOrderCallback,
                  }}
                  savedColumnVisibility={{
                    savedVisibilty: savedVisibleColumns,
                    saveVisibilityCallback: saveVisibleColumnsCallback,
                  }}
                  sortingState={[{ id: "First Name", desc: false }]}
                />
              </>
            )}
          </div>
        </ContextMenu>
      </div>
    </Permission>
  );
};

export default UserTable;
